import { LaunchDarklyClientProvider } from "@openfeature/launchdarkly-client-provider";
import {
  EvaluationContext,
  type Hook,
  InMemoryProvider,
  OpenFeature,
  ProviderEvents,
} from "@openfeature/web-sdk";

import {
  extractFlagConfig,
  getFlagValue,
  type ExtractVariantType,
} from "./utils";

type OpenFeatureFlagConfiguration = ConstructorParameters<
  typeof InMemoryProvider
>[0];

interface FeatureFlagConfiguration {
  [key: string]: {
    disabled?: boolean;
    variants: {
      [variantName: string]: boolean | string;
    };
    defaultVariant: string;
    contextEvaluator?: (context: Record<string, unknown>) => string;
  };
}

/**
 * A single feature-flag evaluation surfaced to consumers via an OpenFeature
 * `after` hook. Analytics-agnostic so this package stays decoupled from any
 * tracking implementation — wire it to a sink (e.g. `createFlagEvaluationTracker`
 * from `@analytics/client`) at init.
 */
interface FlagEvaluationEvent {
  flagKey: string;
  value: boolean | string;
  variant?: string;
  reason?: string;
}

interface FeatureFlagConfig {
  clientSideId?: string;
  flags?: FeatureFlagConfiguration;
  options?: {
    streaming?: boolean;
  };
  /** Invoked after every successful flag evaluation (boolean/string values). */
  onEvaluation?: (event: FlagEvaluationEvent) => void;
}

function createEvaluationTrackingHook(
  onEvaluation: (event: FlagEvaluationEvent) => void
): Hook {
  return {
    after(_hookContext, evaluationDetails) {
      const { flagKey, value, variant, reason } = evaluationDetails;
      // We only model boolean/string flags (see useFlag); ignore other shapes.
      if (typeof value === "boolean" || typeof value === "string") {
        onEvaluation({ flagKey, value, variant, reason });
      }
    },
  };
}

function initializeFeatureFlags(config: FeatureFlagConfig) {
  const { clientSideId, flags = {}, options = {}, onEvaluation } = config;

  // Register a global evaluation hook once, before the provider is set, so every
  // flag evaluation (across all OpenFeature clients) is forwarded to the sink.
  if (onEvaluation) {
    OpenFeature.addHooks(createEvaluationTrackingHook(onEvaluation));
  }

  OpenFeature.setLogger({
    error: (message, ...args) =>
      console.error("[FeatureFlags]", message, ...args),
    warn: (message, ...args) =>
      console.warn("[FeatureFlags]", message, ...args),
    info: (message, ...args) =>
      console.info("[FeatureFlags]", message, ...args),
    debug: () => {},
  });

  if (!clientSideId) {
    // Use InMemoryProvider when no client ID provided
    return OpenFeature.setProviderAndWait(
      new InMemoryProvider(flags as OpenFeatureFlagConfiguration)
    );
  }

  const provider = new LaunchDarklyClientProvider(clientSideId, {
    streaming: options.streaming ?? true,
  });

  // Set up error handling for fallback
  const handleProviderError = (eventDetails: unknown) => {
    console.error(
      "LaunchDarkly provider error, falling back to InMemoryProvider:",
      eventDetails
    );
    void OpenFeature.setProviderAndWait(
      new InMemoryProvider(flags as OpenFeatureFlagConfiguration)
    );
  };

  OpenFeature.addHandler(ProviderEvents.Error, handleProviderError);
  return OpenFeature.setProviderAndWait(provider);
}

// Context helper - accepts any object
// Returns a promise that resolves when the provider has processed the context change
function setContext(context: Record<string, unknown>) {
  return OpenFeature.setContext(context as EvaluationContext);
}

// Get flag value outside of React components
function getFlag<T extends NonNullable<FeatureFlagConfiguration>>(
  flagConfig: T
): ExtractVariantType<T> {
  const [flagKey, defaultValue] = extractFlagConfig(flagConfig);
  const client = OpenFeature.getClient();

  return getFlagValue<ExtractVariantType<T>>(flagKey, defaultValue, {
    boolean: (key, defaultVal) => client.getBooleanValue(key, defaultVal),
    string: (key, defaultVal) => client.getStringValue(key, defaultVal),
  });
}

export { initializeFeatureFlags, setContext, getFlag };

export type {
  FeatureFlagConfiguration,
  FlagEvaluationEvent,
  OpenFeatureFlagConfiguration,
};
